import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { navigationClient } from '../lib/navigation';
import { useSection } from './useSection';
import { logger } from '../lib/logger';

type ImageContext = 'detailCard' | 'listCard' | 'infoWindow' | 'placeCard';

export const useImageDisplay = (
  itemImage: string | undefined,
  context: ImageContext
): { displayImage: string | null; shouldDisplay: boolean } => {
  const section = useSection();
  
  // Récupérer la navigation pour obtenir la configuration
  const { data: navigation } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => navigationClient.getNavigation(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  // Déterminer si l'image doit être affichée selon le contexte
  const { shouldDisplay, displayImage } = useMemo(() => {
    // Trouver la section actuelle
    const currentSection = navigation?.find(navItem => 
      navItem.id === section.id
    );

    // Log pour le débogage
    logger.debug('useImageDisplay evaluation', {
      category: 'Images',
      data: {
        sectionId: section.id,
        sectionType: section.type_rubrique,
        context,
        useImg: currentSection?.use_img,
        hasItemImage: !!itemImage,
        hasReplaceImg: !!currentSection?.replace_img
      }
    });

    // Si use_img est 'none', ne jamais afficher d'image
    if (currentSection?.use_img === 'none') {
      return { shouldDisplay: false, displayImage: null };
    }

    // Si use_img est 'card', afficher uniquement dans les cartes de liste
    if (currentSection?.use_img === 'card') {
      if (context !== 'listCard') {
        return { shouldDisplay: false, displayImage: null };
      }
    }

    // Si use_img est 'detailCard', afficher uniquement dans les cartes de détail
    if (currentSection?.use_img === 'detailCard') {
      if (context !== 'detailCard') {
        return { shouldDisplay: false, displayImage: null };
      }
    }

    // Par défaut, afficher l'image
    // Utiliser l'image de l'item ou l'image de remplacement
    const finalImage = itemImage || currentSection?.replace_img?.[0]?.url || null;
    return { shouldDisplay: true, displayImage: finalImage };
  }, [navigation, section, context, itemImage]);

  return { shouldDisplay, displayImage };
};